import { Injectable, inject } from '@angular/core';
import { MetaDefinition } from '@angular/platform-browser';
import { envs } from '@environments';
import { ArtistInfo, Program } from '@shared/graphql/api.generated';
import { ogSiteName } from '@shared/helper/configs';
import { LinkDefinition } from './link.service';
import { SchemaService } from './schema.service';
import { SeoService } from './seo.service';

@Injectable()
export class ArtistSeoService {
  private readonly seoService = inject(SeoService);
  private readonly schemaService = inject(SchemaService);

  setSeoTags(artist: ArtistInfo) {
    this.seoService.removeMetaTags();
    this.setTitle(artist);
    this.setMetaTags(artist);
    this.setLinkTags(artist);
  }

  private handleArtistTitle(artist: ArtistInfo) {
    return `تماشا آنلاین آثار ${artist.fullName} + بیوگرافی`;
  }

  private handleArtistDescription(artist: ArtistInfo) {
    const bio: string =
      artist.biography.length > 250 ? artist.biography.slice(0, 250) + '...' : artist.biography;
    return (
      `آثار ${artist.fullName} (${artist.fullNameEnglish}) را در پلتفرم شیدا تماشا کنید.` + bio
    );
  }

  private handleArtistKeywords(artist: ArtistInfo) {
    return `فیلم، سریال، ${artist.fullName}، آثار ${artist.fullName}، فیلم های ${artist.fullName}، سریال های ${artist.fullName}`;
  }

  private handleArtistUrl(artist: ArtistInfo) {
    return `${envs.siteUrl}/a/${artist.descriptor}`;
  }

  private setTitle(artist: ArtistInfo) {
    this.seoService.setTitle(this.handleArtistTitle(artist));
  }

  private setMetaTags(artist: ArtistInfo) {
    const tags: MetaDefinition[] = [
      {
        name: 'title',
        content: this.handleArtistTitle(artist),
      },
      {
        name: 'description',
        content: this.handleArtistDescription(artist),
      },
      {
        name: 'keywords',
        content: this.handleArtistKeywords(artist),
      },
      {
        property: 'og:url',
        content: this.handleArtistUrl(artist),
      },
      {
        property: 'og:title',
        content: this.handleArtistTitle(artist),
      },
      {
        property: 'og:type',
        content: 'profile',
      },
      {
        property: 'og:description',
        content: this.handleArtistDescription(artist),
      },
      {
        property: 'og:image',
        content: artist.bannerImagePath!,
      },
      {
        property: 'og:site_name',
        content: ogSiteName,
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:title',
        content: this.handleArtistTitle(artist),
      },
      {
        name: 'twitter:description',
        content: this.handleArtistDescription(artist),
      },
      {
        name: 'twitter:image',
        content: artist.bannerImagePath!,
      },
    ];
    this.seoService.setMetaTags(tags);
  }

  private setLinkTags(artist: ArtistInfo) {
    const tags: LinkDefinition[] = [
      {
        rel: 'image_src',
        href: artist.bannerImagePath!,
      },
      {
        rel: 'thumbnailUrl',
        href: artist.bannerImagePath!,
      },
      {
        rel: 'canonical',
        href: this.handleArtistUrl(artist),
      },
    ];
    this.seoService.setLinkTag(tags);
  }

  setArtistRelatedJsonLdScripts(artistInfo: ArtistInfo) {
    this.setPersonJsonLdScript(artistInfo);
    this.setBreadCrumbsJsonLdScript(artistInfo);
  }

  private setPersonJsonLdScript(artistInfo: ArtistInfo) {
    this.schemaService.setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'Person',
      url: `${envs.siteUrl}/a/${artistInfo.descriptor}`,
      name: artistInfo.fullName,
      image: artistInfo.imagePath,
      description: artistInfo.biography,
      // TODO: Add job title
      // "jobTitle": ["Producer", "Actor", "Executive"]
    });
  }

  setMovieCarouselJsonLdScript(programs: Program[]) {
    this.schemaService.setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: programs.map((program, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${envs.siteUrl}/p/${program.uid}`,
      })),
    });
  }

  private setBreadCrumbsJsonLdScript(artistInfo: ArtistInfo) {
    this.schemaService.setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'شیدا',
          item: `${envs.siteUrl}`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: artistInfo.fullName,
        },
      ],
    });
  }
}
